import React, { useContext, useEffect, useState } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { selectVulnDetail } from 'store/ManageGeneral/selectors';
import { FormattedMessage } from 'react-intl';
import {
  Wrapper, FieldContainer, FieldName, FieldValue,
  MoveIcon, ModalContent, Subtitle, Title, Flex, Footer,
  MoveIconContainer
} from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/HostData/styled';
import { closeModal, openModal } from 'store/modals/actions';
import { MODAL_MOVE_VULN_HOST } from 'store/modals/modals';
import { selectServicesAndHostsContextMove } from 'store/ManageEditCreate/selectors';
import { updateHostParent } from 'store/Manage/actions';
import ModalWrapper from 'Common/Components/ModalWrapper';
import { Dropdown } from 'Common/Components/Inputs';
import { dropdownBlueStyle } from 'Common/Components/Inputs/styled';
import { Accept, Cancel } from 'Common/Components/Button/styled';
import { getHosts, getServices } from 'Screens/Contextualization/ManageEditCreate/actions/Actions';
import { unSelectAll, hideManageDetail } from 'store/Contextualization/AssetDetail/actions';
import { selectCurrentHost } from 'store/Host/selectors';
import { redirectToVulnDetailGeneral } from 'store/ManageGeneral/actions';
import DateFormatter from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/Date';
import moment from 'moment';
import { ThemeContext } from 'Context';
import { AssetIcon } from './styled';

// eslint-disable-next-line import/prefer-default-export
export const HostData = () => {
  const vulnDetail = useSelector(selectVulnDetail);
  const dispatch = useDispatch();
  const handleMoveIconClick = () => {
    dispatch(openModal(MODAL_MOVE_VULN_HOST));
  };

  return (
    <Wrapper>
      <Workspace vulnDetail={ vulnDetail } />
      <Target />
      <Service />
      <Date />
      <LastDetected />
      <MoveIconContainer>
        <MoveIcon title="Move to a different Asset" onClick={ handleMoveIconClick } />
      </MoveIconContainer>
    </Wrapper>
  );
};

export const MoveVulnModal = () => {
  const dispatch = useDispatch();
  const vulnDetail = useSelector(selectVulnDetail);
  const [assets, setAssets] = useState([]);
  const servicesAndHost = useSelector(selectServicesAndHostsContextMove);
  const isWeb = get(vulnDetail, 'type', '') === 'VulnerabilityWeb';
  const currentHost = useSelector(selectCurrentHost);
  const assetId = get(currentHost, 'id', 0);
  const { themeStyles } = useContext(ThemeContext);

  const currentVuln = {
    value: {
      hostId: assetId,
      name: vulnDetail.service?.name || vulnDetail.target,
      type: vulnDetail.type,
      parentId: vulnDetail.parent
    },
    label: vulnDetail.service ? `(${vulnDetail.service.name}) - ${vulnDetail.target}` : vulnDetail.target
  };
  const [selectedAsset, setSelectedAsset] = useState(currentVuln);

  const parseLabel = (item) => {
    if (isWeb) {
      return `(${item.name}) - ${item.hostname}`;
    }
    return item.name;
  };

  const parseHostAndServices = (data) => {
    const filteredData = data.filter(item => {
      if (isWeb) {
        return item.type === 'Service';
      } else if (!isWeb && (vulnDetail?.parent_type === 'Service')) {
        return item;
      } else {
        return item.type === 'Host';
      }
    });
    const result = filteredData.map(item => {
      const parentId = item.id;
      const hostId = isWeb ? item.host_id : item.id;
      return { value: { parentId, type: item.type, name: item.name, hostId }, label: parseLabel(item) };
    });
    return result;
  };

  useEffect(() => {
    return () => dispatch(redirectToVulnDetailGeneral(vulnDetail._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAssets(parseHostAndServices(servicesAndHost));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesAndHost]);

  useEffect(() => {
    dispatch(getServices(vulnDetail.workspace_name));
    dispatch(getHosts(false, vulnDetail.workspace_name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAssetSelect = (asset) => {
    setSelectedAsset(asset);
  };

  const handleAccept = () => {
    const newParent = selectedAsset.value.parentId;
    const newParentType = selectedAsset.value.type;
    dispatch(updateHostParent(vulnDetail._id, newParent, newParentType, vulnDetail.workspace_name));
    if (selectedAsset.value.hostId !== assetId) {
      dispatch(unSelectAll('vulns'));
      dispatch(hideManageDetail());
    }
    dispatch(closeModal(MODAL_MOVE_VULN_HOST));
  };

  const handleClose = () => {
    dispatch(closeModal(MODAL_MOVE_VULN_HOST));
  };

  return (
    <ModalWrapper>
      <ModalContent>
        <Flex>
          <AssetIcon />
          <Title>Move Vulnerability</Title>
        </Flex>
        <Subtitle>Select the Asset where you want to move this vulnerability to</Subtitle>
        <Dropdown
          style={ dropdownBlueStyle(themeStyles) }
          options={ assets }
          defaultValue={ currentVuln }
          onChange={ handleAssetSelect }
        />
        <Footer>
            <Accept
              onClick={ handleAccept }
              disabled={ selectedAsset.value.parentId === currentVuln.value.parentId }
              children="Move"
            />
            <Cancel onClick={ handleClose } children="Close" />
        </Footer>
      </ModalContent>
    </ModalWrapper>
  );
};

const Target = () => {
  const vuln = useSelector(selectVulnDetail);
  const target = get(vuln, 'target', '');

  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.hostData.host" />
      </FieldName>
      <FieldValue title={ target }>{target}</FieldValue>
    </FieldContainer>
  );
};

const Service = () => {
  const vuln = useSelector(selectVulnDetail);
  const service = get(vuln, 'service', null);

  if (!service) return null;
  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.service" />
      </FieldName>
      <FieldValue title={ service.summary }>{service.summary}</FieldValue>
    </FieldContainer>
  );
};

const Date = () => {
  const vuln = useSelector(selectVulnDetail);
  const createTime = get(vuln, 'metadata.create_time', '');
  const today = moment();
  const isToday = (d) => moment(d).isSame(today, 'day');
  const shortDateFormat = (d) => moment(d).format('LT');
  const longDateFormat = (d) => moment(d).format('lll');
  const dateFormatted = isToday(createTime) ? `Today, ${shortDateFormat(createTime)}` : longDateFormat(createTime);

  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.hostData.date" />
      </FieldName>
      <FieldValue title={ dateFormatted }><DateFormatter date={ createTime } /></FieldValue>
    </FieldContainer>
  );
};

const LastDetected = () => {
  const vuln = useSelector(selectVulnDetail);
  const createTime = get(vuln, 'metadata.create_time', '');
  const lastDetected = get(vuln, 'last_detected', '');
  const shouldShowLastDetected = Boolean(createTime && lastDetected && !moment(lastDetected).isSame(createTime, 'day'));

  if (!shouldShowLastDetected) return null;

  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.hostData.lastDetected" />
      </FieldName>
      <FieldValue title={ lastDetected }>
        <DateFormatter date={ lastDetected } />
      </FieldValue>
    </FieldContainer>
  );
};

const Workspace = () => {
  const vuln = useSelector(selectVulnDetail);
  const workspaceName = get(vuln, 'workspace_name', '');

  return (
    <FieldContainer>
      <FieldName>
        <FormattedMessage id="manage.detail.tab.general.hostData.workspace" />
      </FieldName>
      <FieldValue title={ workspaceName }>{ workspaceName }</FieldValue>
    </FieldContainer>
  );
};
